/**
 * @file BlendSavedList.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-10-22
 * @description The Saved Blends tab — what has been kept, and what to do with it.
 *
 *              Loading a blend puts it on the Calculator tab rather than opening
 *              an editor here: there is one place where a blend is built, and it
 *              already has the grid, the live analysis and the export.
 *
 *              Saves are append-only versions, so a blend's history is intact
 *              and "View history" shows every change through the shared audit
 *              modal.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import usePersistedState from '../hooks/usePersistedState';
import Input from './Input';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useDragSelect from '../hooks/useDragSelect';
import AuditHistoryModal from './AuditHistoryModal';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { formatDateTimeLondon } from '../utils/dateFormatters';
import './SearchLayout.css';
import './TableStyles.css';
import './BlendCalculator.css';

const KINDS = [
  { value: '', label: 'All kinds' },
  { value: 'draft', label: 'Drafts' },
  { value: 'customer', label: 'Customer blends' },
  { value: 'house_product', label: 'House products' }
];

export default function BlendSavedList({ user, onLoadBlend }) {
  const [search, setSearch] = usePersistedState('blendSaved.search', '');
  const [kind, setKind] = usePersistedState('blendSaved.kind', '');
  const [archived, setArchived] = usePersistedState('blendSaved.archived', false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [historyFor, setHistoryFor] = useState(null);
  // Every saved version of the blend whose history is open, newest first, so
  // each history record can offer the recipe as it stood before it.
  const [historyVersions, setHistoryVersions] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // The blend about to be emailed to feed@ (asked first — it is an email).
  const [sendFor, setSendFor] = useState(null);
  const [sending, setSending] = useState(false);

  /**
   * Open the history, and fetch the blend's saved versions with it.
   *
   * pricing.blend_versions holds a full material_values snapshot per save, so
   * "the recipe before this change" is a stored version rather than something
   * reconstructed by walking the audit diff backwards — which would have to map
   * the diff's display names ("Rolled Wheat %") back to material keys and would
   * break the moment a material was renamed.
   */
  const openHistory = useCallback(async (blend) => {
    setHistoryFor(blend);
    setHistoryVersions([]);
    try {
      const res = await fetch(`${API_URL}/api/blends/${blend.id}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setHistoryVersions(data.versions || []);
    } catch {
      // A failed fetch leaves the buttons disabled, which is the honest state —
      // the history itself still opens and reads normally.
    }
  }, []);

  /** The newest version saved strictly BEFORE this history record. */
  const versionBefore = useCallback((entry) => {
    const at = new Date(entry.created_at).getTime();
    // historyVersions is newest-first, so the first one older than the entry is
    // the one that was current when the entry was written.
    return historyVersions.find((v) => new Date(v.created_at).getTime() < at) || null;
  }, [historyVersions]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200', archived: String(archived) });
      if (search.trim()) params.set('search', search.trim());
      if (kind) params.set('kind', kind);
      const res = await fetch(`${API_URL}/api/blends/search?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load blends');
      const data = await res.json();
      setRows(data.blends || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, kind, archived]);

  useEffect(() => {
    const t = setTimeout(load, 300);   // debounce the search box
    return () => clearTimeout(t);
  }, [load]);

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    })
  });

  // Archive rather than delete: the blend moves under the Archived toggle,
  // versions and all, and can be restored from there.
  const setArchivedFor = useCallback(async (blend, toArchived) => {
    try {
      const res = await fetch(`${API_URL}/api/blends/${blend.id}/archive`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: toArchived })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Archive failed');
      setNotice(data.message);
      setConfirmDelete(null);
      load();
    } catch (e) {
      setError(e.message);
      setConfirmDelete(null);
    }
  }, [load]);

  /**
   * Email a saved blend to the feed desk (Cam, 2026-10-22 — "reps can right
   * click a saved blend from the table and send to feed@"). The same send as
   * the calculator's after-save offer: POST /api/blends/:id/notify, from the
   * person sending, with the supplier specification PDF attached. Reps may use
   * it — the Blend Calculator is their write carve-out, and the route's
   * router.param('id') keeps them to their own blends and their patch.
   */
  const sendToFeed = useCallback(async (blend) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/blends/${blend.id}/notify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'The email could not be sent');
      setNotice(data.specAttached === false && data.specNote
        ? `"${blend.name}" sent to feed@excel-agri.com. ${data.specNote}`
        : `"${blend.name}" sent to feed@excel-agri.com with the supplier specification attached.`);
      setError(null);
      load();
    } catch (e) {
      setError(`"${blend.name}" was not sent to feed@: ${e.message}`);
    } finally {
      setSending(false);
      setSendFor(null);
    }
  }, [load]);

  const materialCount = (b) => Object.keys(b.material_values || {}).length;

  const [sort, setSort] = usePersistedState('blendSaved.sort', { field: 'latest_version_at', dir: 'desc' });

  const sortedRows = useMemo(() => {
    const value = (b) => {
      switch (sort.field) {
        case 'materials': return materialCount(b);
        case 'latest_version_at': return b.latest_version_at ? new Date(b.latest_version_at).getTime() : 0;
        // Never-sent sorts as 0, so descending puts the most recently sent
        // first and the ones nobody has told the feed desk about last.
        case 'last_sent_at': return b.last_sent_at ? new Date(b.last_sent_at).getTime() : 0;
        default: return String(b[sort.field] ?? '').toLowerCase();
      }
    };
    return [...rows].sort((a, b) => {
      const av = value(a); const bv = value(b);
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sort]);
  const toggleSort = (field) =>
    setSort((prev) => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const sortArrow = (field) => (sort.field === field ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const buildRowActions = useCallback((blend) => [
    {
      key: 'load',
      icon: 'open_in_new',
      label: 'Open in the calculator',
      onClick: () => onLoadBlend(blend)
    },
    {
      key: 'send-feed',
      icon: 'forward_to_inbox',
      label: 'Send to feed@',
      title: 'Email this blend to feed@excel-agri.com, with the supplier specification PDF',
      onClick: () => setSendFor(blend)
    },
    {
      key: 'history',
      icon: 'history',
      label: 'View history',
      onClick: () => openHistory(blend)
    },
    blend.is_archived
      ? {
          key: 'archive',
          icon: 'unarchive',
          label: `Restore ${blend.name}`,
          onClick: () => setArchivedFor(blend, false)
        }
      : {
          key: 'archive',
          icon: 'archive',
          label: `Archive ${blend.name}`,
          onClick: () => setConfirmDelete(blend)
        }
  ], [onLoadBlend, openHistory, setArchivedFor]);

  const ctxMenu = useTableContextMenu({
    rows, selectedIds: selected, getRowId: (b) => b.id
  });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  useSlideOutButton([selected.size, loading]);


  return (
    <div className="search-layout blend-calculator blend-saved" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Saved Blends</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>
              Blends kept for reuse. Opening one puts it on the Calculator tab, where it can
              be edited, compared or exported. Every save is a new version, so the history
              is intact.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={load} title="Reload">
                <div className="icon-wrapper"><span className="material-symbols-rounded">refresh</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 175px auto' }}>
          <div className="filter-group">
            <label>Search</label>
            <Input type="text" icon="search" value={search}
                   placeholder="Blend name, type or customer…"
                   onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Kind</label>
            <Dropdown options={KINDS} value={kind} onChange={setKind} />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="blend-saved-archived" type="checkbox" checked={archived}
                       onChange={(e) => setArchived(e.target.checked)} />
                <label htmlFor="blend-saved-archived"></label>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="search-content">
        {error && (
          <div className="blend-warning inline">
            <span className="material-symbols-rounded">error</span>
            <span>{error}</span>
            <button className="btn-icon" onClick={() => setError(null)}><span className="material-symbols-rounded">close</span></button>
          </div>
        )}
        {notice && !error && (
          <div className="blend-notice inline">
            <span className="material-symbols-rounded">check_circle</span>
            <span>{notice}</span>
            <button className="btn-icon" onClick={() => setNotice(null)}><span className="material-symbols-rounded">close</span></button>
          </div>
        )}

        {loading && (
          <div className="loading">
            <span className="material-symbols-rounded spinning">progress_activity</span>
            <span>Loading blends…</span>
          </div>
        )}

        {!loading && !rows.length && (
          <div className="table-empty">
            <span className="material-symbols-rounded">bookmarks</span>
            <span>
              No saved blends yet. Build one on the Calculator tab and use Save to keep it.
            </span>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="table-container">
            <table className="data-table blend-saved-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <TickCheckbox id="blend-saved-all" checked={allSelected}
                                  onChange={toggleAll} ariaLabel="Select all blends" />
                  </th>
                  <th className="sortable" onClick={() => toggleSort('name')}>Blend{sortArrow('name')}</th>
                  <th className="sortable" onClick={() => toggleSort('customer_name')}>Customer{sortArrow('customer_name')}</th>
                  <th className="sortable" onClick={() => toggleSort('rep_name')}>Rep{sortArrow('rep_name')}</th>
                  <th className="sortable" onClick={() => toggleSort('created_by_name')}>Created by{sortArrow('created_by_name')}</th>
                  <th className="num sortable" onClick={() => toggleSort('materials')}>Materials{sortArrow('materials')}</th>
                  <th className="sortable" onClick={() => toggleSort('latest_version_at')}>Last saved{sortArrow('latest_version_at')}</th>
                  <th className="sortable" onClick={() => toggleSort('last_sent_at')}>Sent to feed{sortArrow('last_sent_at')}</th>
                  <th className="actions-col">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {/* A single click opens it — this list exists to get a blend back
                    into the calculator, so making that the primary gesture beats
                    hiding it behind a double click. */}
                {sortedRows.map((blend) => (
                  <tr key={blend.id}
                      onContextMenu={ctxMenu.onRowContextMenu(blend)}
                      onClick={() => onLoadBlend(blend)}
                      className={`blend-saved-row${selected.has(blend.id) ? ' selected' : ''}`}>
                    <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <TickCheckbox
                        id={`blend-saved-${blend.id}`}
                        checked={selected.has(blend.id)}
                        onChange={() => setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(blend.id)) next.delete(blend.id); else next.add(blend.id);
                          return next;
                        })}
                        onMouseDown={dragSelect.onMouseDown(blend.id, selected.has(blend.id))}
                        onMouseEnter={dragSelect.onMouseEnter(blend.id)}
                        ariaLabel={`Select ${blend.name}`}
                      />
                    </td>
                    <td>
                      <span className="blend-material-name">{blend.name}</span>
                      <span className="blend-material-meta">
                        {blend.blend_type || 'no type'}
                        {blend.item_code ? ` · ${blend.item_code}` : ''}
                        {blend.kind === 'house_product' ? ' · house product' : ''}
                      </span>
                    </td>
                    <td>
                      {blend.is_prospect ? (
                        <span className="name-with-badge">
                          <span className="account-badge-inline">NEW</span>
                          <span>New Prospect</span>
                        </span>
                      ) : blend.customer_name ? (
                        <span className="name-with-badge">
                          {blend.customer_account_no && (
                            <span className="account-badge-inline">{blend.customer_account_no}</span>
                          )}
                          <span>{blend.customer_name}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td>{blend.rep_name || '—'}</td>
                    <td>{blend.created_by_name || '—'}</td>
                    <td className="num">{materialCount(blend)}</td>
                    {/* Time as well as date: several versions of a blend can be
                        saved in one afternoon, and the date alone cannot tell
                        them apart. */}
                    <td className="blend-saved-when">
                      {blend.latest_version_at ? formatDateTimeLondon(blend.latest_version_at) : '—'}
                    </td>
                    {/* Whether the feed desk knows about this blend. "Not sent"
                        is the answer that matters — a blend nobody has been
                        told about is the one that gets missed — so it reads as
                        a state rather than an empty cell. */}
                    <td className="blend-saved-when blend-saved-sent">
                      {blend.last_sent_at ? (
                        <>
                          <span className="blend-sent-when">{formatDateTimeLondon(blend.last_sent_at)}</span>
                          <span className="blend-material-meta">
                            {blend.last_sent_by || 'unknown'}
                            {blend.send_count > 1 ? ` · ${blend.send_count} sends` : ''}
                          </span>
                        </>
                      ) : (
                        <span className="blend-not-sent">Not sent</span>
                      )}
                    </td>
                    <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                      <div className="blend-saved-actions">
                        {buildRowActions(blend).map((a) => (
                          <button key={a.key}
                                  className={`btn-icon${a.className === 'action-danger' ? ' btn-danger' : ''}`}
                                  title={a.title || a.label}
                                  onClick={a.onClick}>
                            <span className="material-symbols-rounded">{a.icon}</span>
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[{ key: 'refresh', icon: 'refresh', label: 'Reload', onClick: load }]}
      />

      {historyFor && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistoryFor(null)}
          entityType="blend"
          entityId={historyFor.id}
          entityIdentifier={historyFor.name}
          entryAction={{
            icon: 'history',
            label: 'Open the recipe as it was before this change',
            unavailableTitle: 'Nothing was saved before this — it is the first version',
            isAvailable: (entry) => !!versionBefore(entry),
            onClick: (entry) => {
              const v = versionBefore(entry);
              if (!v) return;
              // Loads read-only into the calculator as an unsaved draft: saving
              // creates a new version rather than rewriting history.
              onLoadBlend({ ...historyFor, versionId: v.id });
              setHistoryFor(null);
            }
          }}
        />
      )}

      {sendFor && (
        <ConfirmDialog
          isOpen
          title="Send to feed@?"
          message={`Email feed@excel-agri.com with "${sendFor.name}" — its customer, materials and quantities, with the supplier specification PDF attached? It is sent from your own address.`}
          confirmText={sending ? 'Sending…' : 'Send'}
          confirmDisabled={sending}
          cancelText="Cancel"
          onConfirm={() => { if (!sending) sendToFeed(sendFor); }}
          onCancel={() => { if (!sending) setSendFor(null); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          isOpen
          title="Archive this blend?"
          message={`"${confirmDelete.name}" moves to Archived, with all its saved versions. Turn on Archived to find it again and restore it.`}
          confirmText="Archive"
          onConfirm={() => setArchivedFor(confirmDelete, true)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
